import { Box, Stack, SxProps, Typography } from '@mui/material';
import { Divider } from '@portals/core/src/components/Divider/Divider';
import { IconButton } from '@portals/core/src/components/IconButton/IconButton';
import { Trash } from '@portals/icons';
import { EstateSearchProps } from '@portals/sip-client-data/src/general/ApiClientTypes';
import { getFixedT } from 'i18next';
import React, { useCallback, useContext } from 'react';

import { CatalogsContext, SavedSearchActionsContext, useIF6Context } from '../../../../context';
import { usePriceFormatter, useRoomFormatter, useSquareMetersFormatter } from '../../../../hooks';
import { BasicHitButton } from '../../../Buttons';
import { styles as searchStyles } from '../SavedSearches.styles';
import { findFilterConfig, getFormattedLabel, getFormattedLabelValuePair } from '../SavedSearches.utils';
import { styles as itemStyles } from './SavedSearchItem.styles';

const t = getFixedT(null, 'core-immobilien');

type StackElementProps = {
  title: string;
  value: string;
};

const StackElement: React.FC<StackElementProps> = ({ title, value }) => (
  <Box sx={itemStyles.stackElement}>
    <Typography variant="body2" component="span">
      {title}
    </Typography>
    <Typography variant="h6" component="span" sx={itemStyles.stackElementvalue}>
      {value}
    </Typography>
  </Box>
);

export type SavedSearchItemProps = {
  searchParams: EstateSearchProps;
  itemKey: string;
  onDelete: (itemKey: string) => void;
};

export const SavedSearchItem: React.FC<SavedSearchItemProps> = ({ searchParams, itemKey, onDelete }) => {
  const { estateTypeId, objectType, marketingType } = searchParams;
  const { estateSubTypes: catalogsEstateSubTypes } = useContext(CatalogsContext);
  const savedSearchFilter = findFilterConfig({ marketingType, estateTypeId, objectType });
  const priceFormatter = usePriceFormatter();
  const squareMetersFormatter = useSquareMetersFormatter();
  const roomFormatter = useRoomFormatter();

  const isIF6Scope = useIF6Context();

  const savedSearch = useContext(SavedSearchActionsContext);
  const hitSavedSearch = savedSearch?.hitSavedSearch ?? undefined;
  const handleDelete = useCallback(() => {
    onDelete(itemKey);
  }, [onDelete, itemKey]);

  const handleHitButtonClick = useCallback(() => {
    hitSavedSearch && hitSavedSearch(searchParams);
  }, [hitSavedSearch, searchParams]);

  return (
    <Stack sx={itemStyles.stack} divider={<Divider sx={itemStyles.stackDivider} />} className="saved-search-item">
      <Box sx={itemStyles.stackElement}>
        <Typography variant="h5">{getFormattedLabel(searchParams, catalogsEstateSubTypes, 'location')}</Typography>
        <IconButton
          className="saved-search-button delete-button"
          size="large"
          onClick={handleDelete}
          sx={itemStyles.deleteButton}
        >
          <Trash />
        </IconButton>
      </Box>
      <StackElement title={t('object')} value={getFormattedLabel(searchParams, catalogsEstateSubTypes, 'object')} />
      {savedSearchFilter?.fields?.map((field) => {
        const { value, label } = getFormattedLabelValuePair(
          field,
          searchParams,
          priceFormatter,
          squareMetersFormatter,
          roomFormatter
        );
        return <StackElement key={label} title={label} value={value} />;
      })}
      <Box sx={{ ...itemStyles.stackElement, ...itemStyles.deleteButtonContainer } as SxProps}>
        <BasicHitButton
          values={searchParams}
          variant="standard"
          color="secondary"
          className="saved-search-button hit-button"
          onClick={isIF6Scope ? handleHitButtonClick : undefined}
          fullWidth
          sx={searchStyles.secondaryButton}
        />
      </Box>
    </Stack>
  );
};

SavedSearchItem.displayName = 'SavedSearchItem';
